// apps/office/src/components/office/InteriorInvoicePDF.jsx
import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import invoiceBg from '../../pages/public/Invoice.png';

const C = {
  ink: '#111827',
  muted: '#6b7280',
  border: '#d1d5db',
};

const S = StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    fontFamily: 'Helvetica',
    fontSize: 10,
    color: C.ink,
  },
  bg: {
    position: 'absolute', top: 0, left: 0,
    width: 595.28, height: 841.89,
  },
  abs: { position: 'absolute' },
  row: { flexDirection: 'row' },
  bold: { fontWeight: 'bold' },
  r: { textAlign: 'right' },
  card: {
    borderWidth: 0.8, borderColor: C.border,
    borderRadius: 10, backgroundColor: '#ffffff',
    overflow: 'hidden',
  },
});

const rs = (v) => 'Rs. ' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(v) || 0);
const s = (v, fb = '-') => (v === null || v === undefined || v === '' ? fb : String(v));
const pArr = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') { try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch (e) { return []; } }
  return [];
};
const dfmt = (d) => {
  if (!d) return '-';
  try { const t = new Date(d); return String(t.getDate()).padStart(2, '0') + '-' + String(t.getMonth() + 1).padStart(2, '0') + '-' + t.getFullYear(); }
  catch (e) { return '-'; }
};

function words(n) {
  n = Math.floor(Number(n) || 0);
  if (!n) return 'Zero rupees only';
  const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const t2 = (x) => x < 20 ? a[x] : b[Math.floor(x / 10)] + (x % 10 ? ' ' + a[x % 10] : '');
  const t3 = (x) => x >= 100 ? a[Math.floor(x / 100)] + ' Hundred' + (x % 100 ? ' ' + t2(x % 100) : '') : t2(x);
  const p = []; let cr = Math.floor(n / 1e7); n %= 1e7;
  let lk = Math.floor(n / 1e5); n %= 1e5; let th = Math.floor(n / 1e3); n %= 1e3;
  if (cr) p.push(t3(cr) + ' Crore'); if (lk) p.push(t3(lk) + ' Lakh');
  if (th) p.push(t3(th) + ' Thousand'); if (n) p.push(t3(n));
  return p.join(' ').trim() + ' rupees only';
}

const InteriorInvoicePDF = ({ quotation, project, client, invoiceNumber }) => {
  const items = pArr(quotation && quotation.items);
  const profitPct = Number(quotation && quotation.profitMargin) || 20;
  const profitFactor = 1 + profitPct / 100;

  const rows = items.map((b) => {
    const prods = pArr(b.products || b.items);
    const mod = prods.reduce((a, x) => a + ((Number(x.amount) || 0) * profitFactor), 0);
    const rl = Number(b.roomLength) || 0, rw = Number(b.roomWidth) || 0;
    return {
      name: s(b.groupName || b.subcategoryName, 'Room'),
      size: rl && rw ? rl + ' x ' + rw : '-',
      area: Number(b.roomArea) || 0,
      mod,
    };
  });

  const sumArea = rows.reduce((a, r) => a + r.area, 0);
  const sumMod = rows.reduce((a, r) => a + r.mod, 0);
  const subTotal = sumMod;
  const discType = (quotation && quotation.discountType) || 'percentage';
  const discValue = Number(quotation && quotation.discountValue) || 0;
  const disc = discType === 'percentage' ? subTotal * discValue / 100 : discValue;
  const initialQuote = subTotal - disc;
  const cgstEnabled = !!(quotation && quotation.cgstEnabled);
  const cgstRate = Number(quotation && quotation.cgstRate) || 9;
  const cgstAmt = cgstEnabled ? initialQuote * cgstRate / 100 : 0;
  const sgstEnabled = !!(quotation && quotation.sgstEnabled);
  const sgstRate = Number(quotation && quotation.sgstRate) || 9;
  const sgstAmt = sgstEnabled ? initialQuote * sgstRate / 100 : 0;
  const finalQuoteValue = initialQuote + cgstAmt + sgstAmt;

  const invDate = dfmt(new Date());
  const visibleRows = rows.slice(0, 8);

  return (
    <Document>
      <Page size="A4" style={S.page}>
        <Image src={invoiceBg} style={S.bg} fixed />

        {/* ═══ 3-COLUMN INFO BLOCK ═══ */}

        {/* ─── COLUMN 1: CLIENT INFORMATION (left, x=45, w=170) ─── */}
        <Text style={[S.abs, S.bold, { top: 200, left: 45, width: 170, fontSize: 11 }]}>
          {s(client && client.name, 'Client')}
        </Text>
        {client && client.phone ? (
          <Text style={[S.abs, { top: 216, left: 45, width: 170, fontSize: 9, color: C.muted }]}>
            {client.phone}
          </Text>
        ) : null}
        {client && client.email ? (
          <Text style={[S.abs, { top: 230, left: 45, width: 170, fontSize: 9, color: C.muted }]}>
            {client.email}
          </Text>
        ) : null}
        {client && client.address ? (
          <Text style={[S.abs, { top: 244, left: 45, width: 170, fontSize: 9, color: C.muted }]}>
            {client.address}
          </Text>
        ) : null}

        {/* ─── COLUMN 2: PROPERTY INFORMATION (middle, x=222, w=170) ─── */}
        <Text style={[S.abs, S.bold, { top: 200, left: 222, width: 170, fontSize: 11 }]}>
          {s(project && project.propertyName, 'Property')}
        </Text>
        {project && project.designStyle ? (
          <Text style={[S.abs, { top: 216, left: 222, width: 170, fontSize: 9, color: C.muted }]}>
            Style: {project.designStyle}
          </Text>
        ) : null}
        {project && project.propertyAddress ? (
          <Text style={[S.abs, { top: 230, left: 222, width: 170, fontSize: 9, color: C.muted }]}>
            {project.propertyAddress}
          </Text>
        ) : null}

        {/* ─── COLUMN 3: INVOICE DETAILS (right, x=400, w=160) ─── */}
        <Text style={[S.abs, S.bold, { top: 200, left: 400, width: 160, fontSize: 11 }]}>
          {invoiceNumber || 'INV-XXXXX'}
        </Text>
        <Text style={[S.abs, { top: 216, left: 400, width: 160, fontSize: 9, color: C.muted }]}>
          Date: {invDate}
        </Text>
        {client && client.jobId ? (
          <Text style={[S.abs, { top: 230, left: 400, width: 160, fontSize: 9, color: C.muted }]}>
            Job ID: {client.jobId}
          </Text>
        ) : null}
        {quotation && quotation.quoteNumber ? (
          <Text style={[S.abs, { top: 244, left: 400, width: 160, fontSize: 9, color: C.muted }]}>
            Quote Ref: {quotation.quoteNumber}
          </Text>
        ) : null}

        {/* ═══ SINGLE CARD — Room Summary + Discount + Initial + GST + Final ═══ */}
        <View style={[S.abs, { top: 280, left: 40, width: 515 }, S.card]}>
          {/* Table header */}
          <View style={[S.row, { paddingVertical: 8, paddingHorizontal: 12, borderBottomWidth: 0.5, borderBottomColor: '#e5e7eb', backgroundColor: '#f9fafb' }]}>
            <Text style={[S.bold, { flex: 2.2, fontSize: 9 }]}>Room Name</Text>
            <Text style={[S.bold, { flex: 1.2, fontSize: 9 }]}>Room Size</Text>
            <Text style={[S.bold, { flex: 1.2, fontSize: 9 }]}>Room Area</Text>
            <Text style={[S.bold, S.r, { flex: 1.3, fontSize: 9 }]}>Modules</Text>
          </View>

          {/* Data rows */}
          {visibleRows.map((r, i) => (
            <View key={i} style={[S.row, { paddingVertical: 7, paddingHorizontal: 12, borderBottomWidth: i < visibleRows.length - 1 ? 0.4 : 0, borderBottomColor: '#f3f4f6' }]}>
              <Text style={{ flex: 2.2, fontSize: 9 }}>{r.name}</Text>
              <Text style={{ flex: 1.2, fontSize: 9 }}>{r.size}</Text>
              <Text style={{ flex: 1.2, fontSize: 9 }}>{r.area ? r.area + ' sqft.' : '-'}</Text>
              <Text style={[S.r, S.bold, { flex: 1.3, fontSize: 9 }]}>{rs(r.mod)}</Text>
            </View>
          ))}

          {/* Sub Total */}
          <View style={[S.row, { paddingVertical: 8, paddingHorizontal: 12, borderTopWidth: 0.6, borderTopColor: '#9ca3af', backgroundColor: '#ffffff' }]}>
            <Text style={[S.bold, { flex: 2.2, fontSize: 10 }]}>Sub Total :</Text>
            <Text style={{ flex: 1.2 }}></Text>
            <Text style={[S.bold, { flex: 1.2, fontSize: 10 }]}>{sumArea} sqft.</Text>
            <Text style={[S.bold, S.r, { flex: 1.3, fontSize: 10 }]}>{rs(subTotal)}</Text>
          </View>

          {/* Discount row */}
          {disc > 0 ? (
            <View style={[S.row, { paddingVertical: 7, paddingHorizontal: 12, borderTopWidth: 0.4, borderTopColor: '#e5e7eb', backgroundColor: '#ffffff' }]}>
              <Text style={{ flex: 3, fontSize: 10, paddingLeft: 100 }}>
                Discount {discType === 'percentage' ? '(' + discValue + '%)' : '(' + ((disc / subTotal) * 100).toFixed(1) + '%)'}
              </Text>
              <Text style={[S.r, S.bold, { flex: 2, fontSize: 10 }]}>- {rs(disc)}</Text>
            </View>
          ) : null}

          {/* Initial Quote Value row */}
          <View style={[S.row, { paddingVertical: 10, paddingHorizontal: 12, borderTopWidth: 0.6, borderTopColor: '#9ca3af', backgroundColor: '#f9fafb' }]}>
            <Text style={[S.bold, { flex: 3, fontSize: 11 }]}>Initial Quote Value :</Text>
            <Text style={[S.r, S.bold, { flex: 2, fontSize: 11 }]}>{rs(initialQuote)}</Text>
          </View>
          {cgstAmt > 0 ? (
            <View style={[S.row, { paddingVertical: 8, paddingHorizontal: 12, backgroundColor: '#ffffff' }]}>
              <Text style={{ flex: 3, fontSize: 10, paddingLeft: 100 }}>CGST @ {cgstRate}%</Text>
              <Text style={[S.r, S.bold, { flex: 2, fontSize: 10 }]}>+ {rs(cgstAmt)}</Text>
            </View>
          ) : null}
          {sgstAmt > 0 ? (
            <View style={[S.row, { paddingVertical: 8, paddingHorizontal: 12, borderTopWidth: 0.4, borderTopColor: '#e5e7eb', backgroundColor: '#ffffff' }]}>
              <Text style={{ flex: 3, fontSize: 10, paddingLeft: 100 }}>SGST @ {sgstRate}%</Text>
              <Text style={[S.r, S.bold, { flex: 2, fontSize: 10 }]}>+ {rs(sgstAmt)}</Text>
            </View>
          ) : null}

          {/* Final Quote Value highlighted */}
          <View style={[S.row, { paddingVertical: 12, paddingHorizontal: 12, backgroundColor: '#fffbeb', borderTopWidth: 1, borderTopColor: '#f59e0b' }]}>
            <Text style={{ flex: 3, fontSize: 12, fontWeight: 'bold', color: '#78350f' }}>FINAL QUOTE VALUE</Text>
            <Text style={[S.r, { flex: 2, fontSize: 13, fontWeight: 'bold', color: '#78350f' }]}>{rs(finalQuoteValue)}</Text>
          </View>
        </View>

        {/* ═══ AMOUNT IN WORDS ═══ */}
        <View style={[S.abs, { top: 620, left: 40, width: 515, borderWidth: 0.6, borderColor: C.border, borderRadius: 8, backgroundColor: '#ffffff', paddingVertical: 7, paddingHorizontal: 12 }]}>
          <Text style={{ fontSize: 9, fontWeight: 'bold', color: '#374151', textAlign: 'center' }}>
            {words(finalQuoteValue).toUpperCase()}
          </Text>
        </View>

      </Page>
    </Document>
  );
};

export default InteriorInvoicePDF;
